import { Loader2, Network } from "lucide-react";
import { type FormEvent, useCallback, useEffect, useId, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import {
	Dialog,
	DialogBody,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { statusClasses } from "@/lib/colors";
import { cn } from "@/lib/utils";
import {
	discoveryErrorLabel,
	driverNotes,
	formatDateTime,
	GENERIC_DRIVER_NOTE,
	IMMEDIATE_DIALOG_NOTE,
	isIpv4,
	networkAddUnavailableReason,
	networkPrinterAddress,
	networkPrinterSource,
	PLAN_DIALOG_NOTE,
	printerNameError,
} from "./printer-labels";
import type { PlanAction, PlanNames } from "./printer-plan";
import type { NetworkPrinterCandidate, PrinterDevice } from "./printers-api";
import {
	DISCOVERY_MAX_POLL_MS,
	printerErrorMessage,
	useDiscoverMutation,
	useNetworkPrintersQuery,
} from "./use-printers-query";

/** Pozycja listy: backend scala źródła po adresie, ale ten sam host może mieć RAW i LPR. */
function candidateKey(printer: NetworkPrinterCandidate): string {
	return `${printer.protocol}|${printer.host}|${printer.port}|${printer.queue ?? ""}`;
}

/**
 * „Dodaj drukarkę sieciową”: wykrywanie drukarek w sieci komputera (od razu
 * po otwarciu, „Wykryj ponownie”, „Sprawdź adres”), wybór pozycji, nazwy
 * kolejki i sterownika. Okno dopisuje `add_printer` do planu. Zamknięcie okna
 * kończy odpytywanie.
 * `initialSetDefault` — „Ustaw jako domyślną” zaznaczone na start (z „Drukarka domyślna…”);
 * `initialHost` — drukarka zaznaczona na start (z mapy: drukarka sieciowa → stanowisko).
 */
export function AddNetworkPrinterDialog({
	device,
	initialSetDefault = false,
	initialHost,
	onPlan,
	onClose,
	immediate = false,
}: {
	device: PrinterDevice | null;
	initialSetDefault?: boolean;
	initialHost?: string;
	onPlan: (action: PlanAction, names: PlanNames) => void;
	onClose: () => void;
	/** Panel helpdesku: „Wykonaj” startuje operację od razu, bez planu. */
	immediate?: boolean;
}) {
	return (
		<Dialog open={device != null} onOpenChange={(next) => (next ? undefined : onClose())}>
			<DialogContent className="max-w-xl">
				{device ? (
					// Inny komputer = świeże wykrywanie i wybór.
					<AddNetworkPrinterBody
						key={`${device.deviceUuid}:${initialHost ?? ""}`}
						device={device}
						initialSetDefault={initialSetDefault}
						initialHost={initialHost}
						onPlan={onPlan}
						onClose={onClose}
						immediate={immediate}
					/>
				) : null}
			</DialogContent>
		</Dialog>
	);
}

function AddNetworkPrinterBody({
	device,
	initialSetDefault,
	initialHost,
	onPlan,
	onClose,
	immediate,
}: {
	device: PrinterDevice;
	initialSetDefault: boolean;
	initialHost?: string;
	onPlan: (action: PlanAction, names: PlanNames) => void;
	onClose: () => void;
	immediate: boolean;
}) {
	const ids = useId();
	const discover = useDiscoverMutation(device.deviceUuid);
	// Lista dopiero po pierwszym starcie wykrywania — odczyt sprzed niego miałby
	// stary stan wykrywania i od razu zatrzymałby odpytywanie.
	const [ready, setReady] = useState(false);
	const [polling, setPolling] = useState(false);
	const [round, setRound] = useState(0);
	const query = useNetworkPrintersQuery(device.deviceUuid, ready, polling);

	const [checkInput, setCheckInput] = useState("");
	const [checkError, setCheckError] = useState<string | null>(null);
	const [selectedKey, setSelectedKey] = useState<string | null>(null);
	const [name, setName] = useState("");
	const [driverName, setDriverName] = useState<string | null>(null);
	const [setDefault, setSetDefault] = useState(initialSetDefault);

	const discoverMutate = discover.mutate;
	const startDiscovery = useCallback(
		(checkHost?: string) => {
			discoverMutate(checkHost, {
				onSuccess: () => {
					setPolling(true);
					setRound((r) => r + 1);
				},
				onSettled: () => setReady(true),
			});
		},
		[discoverMutate],
	);

	// Raz na otwarcie okna (ref przeżywa podwójny efekt StrictMode).
	const discoveryRequested = useRef(false);
	useEffect(() => {
		if (discoveryRequested.current) return;
		discoveryRequested.current = true;
		startDiscovery();
	}, [startDiscovery]);

	// Bezpiecznik: każde wykrywanie odpytujemy najwyżej 20 s.
	useEffect(() => {
		if (round === 0) return;
		const timer = setTimeout(() => setPolling(false), DISCOVERY_MAX_POLL_MS);
		return () => clearTimeout(timer);
	}, [round]);

	const data = query.data;
	const discovery = data?.discovery ?? null;
	const running = discovery?.status === "running";
	const searching = discover.isPending || query.isLoading || (polling && running);
	const stalled = !searching && running && round > 0;
	const unavailable = data ? networkAddUnavailableReason(data) : null;
	const canDiscover = data ? data.online && data.addSupported : true;
	const printers = data?.printers ?? [];
	const selected =
		printers.find((p) => candidateKey(p) === selectedKey && p.installedAs == null) ?? null;
	const drivers = selected?.drivers ?? [];
	const driver = drivers.find((d) => d.name === driverName) ?? drivers[0] ?? null;
	const existingNames = device.printers.filter((p) => p.port !== "").map((p) => p.name);
	const nameError = selected ? printerNameError(name, existingNames) : null;
	const canSubmit = selected != null && driver != null && nameError == null && unavailable == null;

	const select = (printer: NetworkPrinterCandidate) => {
		setSelectedKey(candidateKey(printer));
		setName(printer.suggestedName);
		setDriverName(printer.drivers[0]?.name ?? null);
	};

	// Drukarka wskazana na mapie — zaznaczona, gdy pojawi się na liście (raz).
	const preselected = useRef(false);
	useEffect(() => {
		if (preselected.current || !initialHost) return;
		const match = printers.find((p) => p.host === initialHost && p.installedAs == null);
		if (!match) return;
		preselected.current = true;
		setSelectedKey(candidateKey(match));
		setName(match.suggestedName);
		setDriverName(match.drivers[0]?.name ?? null);
	}, [initialHost, printers]);

	const submitCheck = (event: FormEvent) => {
		event.preventDefault();
		const host = checkInput.trim();
		if (!isIpv4(host)) {
			setCheckError("Wpisz adres IPv4, np. 192.168.1.50.");
			return;
		}
		setCheckError(null);
		startDiscovery(host);
	};

	const submit = () => {
		if (!selected || !driver || nameError) return;
		const printerName = name.trim();
		onPlan(
			{
				kind: "add_printer",
				input: {
					deviceUuid: device.deviceUuid,
					host: selected.host,
					protocol: selected.protocol,
					port: selected.port,
					queue: selected.queue,
					printerName,
					driverName: driver.name,
					setDefault,
				},
			},
			{ device: device.displayName, printer: printerName, extra: networkPrinterAddress(selected) },
		);
		onClose();
	};

	const checked = discovery?.status === "done" ? discovery.checked : null;
	const checkedListed = checked != null && printers.some((p) => p.host === checked.host);

	return (
		<>
			<DialogHeader>
				<DialogTitle>Dodaj drukarkę sieciową</DialogTitle>
				<DialogDescription>
					Drukarka z sieci komputera {device.displayName} — zainstalujemy ją na tym komputerze (port
					TCP/IP i kolejka Windows).
				</DialogDescription>
			</DialogHeader>
			<DialogBody className="space-y-4">
				{data?.network ? (
					<p className="flex items-center gap-2 text-xs text-muted-foreground">
						<Network className="size-3.5 shrink-0" aria-hidden />
						<span className="truncate">
							Sieć komputera:{" "}
							<span className="font-mono text-foreground">{data.network.subnet}</span> · brama{" "}
							<span className="font-mono text-foreground">{data.network.gateway}</span>
						</span>
					</p>
				) : null}
				{unavailable ? (
					<p className={cn("rounded-md border p-2 text-xs", statusClasses.warning)}>
						{unavailable}
					</p>
				) : null}

				<div className="flex flex-wrap items-center gap-2 text-sm">
					<span className="min-w-0 flex-1">
						{searching ? (
							<span role="status" className="flex items-center gap-2 text-muted-foreground">
								<Loader2 className="size-4 animate-spin" aria-hidden />
								Szukam drukarek w sieci…
							</span>
						) : stalled ? (
							<span className="text-xs text-muted-foreground">
								Komputer nie skończył wykrywania w 20 s — kliknij „Wykryj ponownie”.
							</span>
						) : discovery?.status === "failed" ? (
							<span className="text-xs text-status-error-text">
								Wykrywanie nie powiodło się: {discoveryErrorLabel(discovery.error)}.
							</span>
						) : discovery?.status === "done" && discovery.finishedAt ? (
							<span className="text-xs text-muted-foreground">
								Wykrywanie z {formatDateTime(discovery.finishedAt)}
							</span>
						) : null}
					</span>
					<Button
						size="sm"
						variant="outline"
						className="h-7 text-xs max-md:h-9"
						disabled={searching || !canDiscover}
						onClick={() => startDiscovery()}
					>
						Wykryj ponownie
					</Button>
				</div>

				<div className="space-y-1">
					<label htmlFor={`${ids}-check`} className="text-sm font-medium">
						Sprawdź adres
					</label>
					<form onSubmit={submitCheck} className="flex items-center gap-2">
						<Input
							id={`${ids}-check`}
							value={checkInput}
							onChange={(e) => setCheckInput(e.target.value)}
							placeholder="np. 192.168.1.50"
							inputMode="decimal"
							autoComplete="off"
							className="h-8 max-w-48 font-mono"
							aria-invalid={checkError != null}
							aria-describedby={`${ids}-check-hint`}
						/>
						<Button
							type="submit"
							size="sm"
							variant="outline"
							className="h-8 max-md:h-9"
							disabled={searching || !canDiscover}
						>
							Sprawdź
						</Button>
					</form>
					<p
						id={`${ids}-check-hint`}
						className={cn(
							"text-xs",
							checkError ? "text-status-error-text" : "text-muted-foreground",
						)}
					>
						{checkError ??
							"Nie ma drukarki na liście? Wpisz jej adres IP — komputer sprawdzi port 9100."}
					</p>
					{checked ? (
						<p
							className={cn(
								"text-xs",
								checked.open ? "text-status-success-text" : "text-status-warning-text",
							)}
						>
							<span className="font-mono">{checked.host}</span>:{" "}
							{checked.open
								? `port 9100 otwarty${checkedListed ? " — drukarka jest na liście" : ""}.`
								: "port 9100 zamknięty — pod tym adresem nie odpowiada drukarka (albo jest wyłączona)."}
						</p>
					) : null}
					{discover.isError ? (
						<p role="alert" className="text-xs text-status-error-text">
							{printerErrorMessage(discover.error)}
						</p>
					) : null}
				</div>

				{query.isError ? (
					<div role="alert" className="flex flex-wrap items-center gap-2 text-sm">
						<span className="text-status-error-text">
							Nie udało się pobrać listy drukarek: {printerErrorMessage(query.error)}
						</span>
						<Button size="sm" variant="outline" onClick={() => void query.refetch()}>
							Spróbuj ponownie
						</Button>
					</div>
				) : (
					<fieldset className="space-y-1.5">
						<legend className="mb-1 text-sm font-medium">Drukarki w sieci komputera</legend>
						{printers.length === 0 ? (
							searching || !data ? null : (
								<p className="text-sm text-muted-foreground">
									Nie znaleziono drukarek w sieci komputera. Jeśli znasz adres IP drukarki, wpisz go
									w polu „Sprawdź adres”.
								</p>
							)
						) : (
							printers.map((printer, index) => (
								<CandidateRow
									key={candidateKey(printer)}
									inputId={`${ids}-p${index}`}
									name={`${ids}-printer`}
									printer={printer}
									checked={selected != null && candidateKey(selected) === candidateKey(printer)}
									onSelect={() => select(printer)}
								/>
							))
						)}
					</fieldset>
				)}

				{selected ? (
					<div className="space-y-3 rounded-md border p-3">
						<div className="space-y-1">
							<label htmlFor={`${ids}-name`} className="text-sm font-medium">
								Nazwa drukarki
							</label>
							<Input
								id={`${ids}-name`}
								value={name}
								onChange={(e) => setName(e.target.value)}
								autoComplete="off"
								className="h-8"
								aria-invalid={nameError != null}
								aria-describedby={`${ids}-name-hint`}
							/>
							<p
								id={`${ids}-name-hint`}
								className={cn(
									"text-xs",
									nameError ? "text-status-error-text" : "text-muted-foreground",
								)}
							>
								{nameError ?? "Pod tą nazwą drukarka będzie widoczna w Windows."}
							</p>
						</div>

						<fieldset className="space-y-1.5">
							<legend className="mb-1 text-sm font-medium">Sterownik</legend>
							{drivers.length === 0 ? (
								<p className={cn("rounded-md border p-2 text-xs", statusClasses.warning)}>
									Na komputerze nie ma pasującego sterownika ani sterownika ogólnego — tej drukarki
									nie da się teraz dodać. Zainstaluj sterownik producenta i wykryj ponownie.
								</p>
							) : (
								drivers.map((d, index) => {
									const inputId = `${ids}-d${index}`;
									const isChecked = driver?.name === d.name;
									const notes = driverNotes(d);
									return (
										<label
											key={d.name}
											htmlFor={inputId}
											className={cn(
												"flex min-h-9 cursor-pointer items-center gap-2 rounded-md border px-2 py-1.5 text-sm hover:bg-muted/50",
												isChecked && "border-primary bg-primary/5",
											)}
										>
											<input
												id={inputId}
												type="radio"
												name={`${ids}-driver`}
												value={d.name}
												checked={isChecked}
												onChange={() => setDriverName(d.name)}
												className="size-4 shrink-0 accent-primary"
											/>
											<span className="min-w-0 flex-1 truncate">{d.name}</span>
											{notes.length > 0 ? (
												<span className="shrink-0 text-xs text-muted-foreground">
													{notes.join(" · ")}
												</span>
											) : null}
										</label>
									);
								})
							)}
							{driver?.generic ? (
								<p className="text-xs text-muted-foreground">{GENERIC_DRIVER_NOTE}</p>
							) : null}
						</fieldset>

						<div>
							<label className="flex items-center gap-2 text-sm">
								<input
									type="checkbox"
									checked={setDefault}
									onChange={(e) => setSetDefault(e.target.checked)}
									className="size-4 accent-primary"
								/>
								Ustaw jako domyślną
							</label>
							{setDefault ? (
								<p className="pl-6 text-xs text-muted-foreground">
									Domyślną ustawiamy zalogowanemu użytkownikowi Windows — gdy nikt nie jest
									zalogowany albo jest ich kilku, drukarka zostanie dodana bez tego.
								</p>
							) : null}
						</div>
					</div>
				) : null}
				<p className="text-xs text-muted-foreground">
					{immediate ? IMMEDIATE_DIALOG_NOTE : PLAN_DIALOG_NOTE}
				</p>
			</DialogBody>
			<DialogFooter>
				<Button variant="outline" size="sm" onClick={onClose}>
					Anuluj
				</Button>
				<Button size="sm" onClick={submit} disabled={!canSubmit}>
					{immediate ? "Wykonaj" : "Dodaj do planu"}
				</Button>
			</DialogFooter>
		</>
	);
}

function CandidateRow({
	inputId,
	name,
	printer,
	checked,
	onSelect,
}: {
	inputId: string;
	name: string;
	printer: NetworkPrinterCandidate;
	checked: boolean;
	onSelect: () => void;
}) {
	const installed = printer.installedAs != null;
	const source = networkPrinterSource(printer);
	return (
		<label
			htmlFor={inputId}
			className={cn(
				"flex min-h-9 items-start gap-2 rounded-md border px-2 py-1.5 text-sm",
				installed
					? "cursor-not-allowed bg-muted/40 text-muted-foreground"
					: "cursor-pointer hover:bg-muted/50",
				checked && "border-primary bg-primary/5",
			)}
			data-printer-host={printer.host}
		>
			<input
				id={inputId}
				type="radio"
				name={name}
				value={candidateKey(printer)}
				checked={checked}
				disabled={installed}
				onChange={onSelect}
				className="mt-0.5 size-4 shrink-0 accent-primary"
			/>
			<span className="min-w-0 flex-1">
				<span className="flex flex-wrap items-baseline gap-x-2">
					<span className="font-medium break-words">{printer.suggestedName}</span>
					{printer.model && printer.model !== printer.suggestedName ? (
						<span className="truncate text-xs text-muted-foreground">{printer.model}</span>
					) : null}
				</span>
				<span className="block font-mono text-xs text-muted-foreground">
					{networkPrinterAddress(printer)}
				</span>
				{source ? <span className="block text-xs text-muted-foreground">{source}</span> : null}
				{installed ? (
					<span className="block text-xs text-foreground">
						Już zainstalowana jako „{printer.installedAs}”
					</span>
				) : null}
			</span>
		</label>
	);
}
